import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Sidebar from "../../components/Sidebar";
import API_URL from "../../api/API";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";

import WaiverPreview from "../commoncomponents/WaiverPreview";
import ChangeSummary from "./components/ChangeSummary";
import ConfirmModal from "../../components/ConfirmModal";


const WaiverReviewAndPublishEditor = () => {

    const { business } = useBusinessAuthContext();
    const navigate = useNavigate();
    const { waiverId } = useParams();

    const [loading, setLoading] = useState(true);

    const [waiverData, setWaiverData] = useState(null);

    const [governingData, setGoverningData] = useState([]);

    const [device, setDevice] = useState("desktop");

    const [signerType, setSignerType] = useState("adult");

    const [waiverMode, setWaiverMode] = useState("full");

    const [isPublishing, setIsPublishing] = useState(false);

    const [showPublishConfirm, setShowPublishConfirm] = useState(false);

    const [otherMap, setOtherMap] = useState({});

    useEffect(() => {

        if (!business) return;

        const load = async () => {

            try {

                const res = await fetch(
                    `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}?mode=preview`,
                    {
                        headers: {
                            Authorization: `Bearer ${business.token}`,
                            "x-session-id": business.sessionId,
                            "x-frontend-url": window.location.pathname
                        }
                    }
                );

                const data = await res.json();

                if (data.success) {
                    setWaiverData(data);
                }

            }
            finally {
                setLoading(false);
            }

        };

        load();

    }, [waiverId, business]);

    // Same governing-law useEffect from Step-7
    // (You can literally copy it.)

    if (loading) {
        return <div>Loading...</div>;
    }

    const waiver = waiverData?.waiver;

    if (!waiver) {
        return <div>Unable to load waiver.</div>;
    }

    const phValues = {
        BUSINESS_NAME: waiver.businessId.businessName,
        BUSINESS_EMAIL: waiver.businessId.email,
        COUNTY:  waiver?.locations?.[0]?.county || "",
        MINOR_FULL_NAME: "the minor",
        MINOR_FIRST_NAME: "the minor",
        PREVIOUS_SIGNED_DATE: "THE PREVIOUS SIGNED DATE",
    };

    const getPublishConfirmMessage = (status) => {
        if (status === "new_version") {
        return `Once published, this waiver will go live. If you make changes later, we’ll save them as a new version.`;
        }

        return `Once published, this waiver will go live. If you make changes later, we’ll save them as a new version.`;
    };

    const confirmPublish = async () => {

        setShowPublishConfirm(false);

        try {
            const res = await fetch(
                `${API_URL.BUSINESS_WAIVER_PUBLISH}/${waiverId}`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${business.token}`,
                        "x-session-id": business.sessionId,
                        "x-frontend-url": window.location.pathname,
                    },
                }
            );

            const data = await res.json();

            // ---------------------------------------------
            // Eligibility changes require confirmation
            // ---------------------------------------------
            if (
                !data.success &&
                data.code === "ELIGIBILITY_CHANGES_NOT_ACCEPTED"
            ) {
                setIsPublishing(false);

                navigate(
                    `/business/waivereditor/${waiverId}/eligibility`,
                    {
                        state: {
                            showEligibilityConfirm: true,
                        },
                    }
                );

                return;
            }

            // ---------------------------------------------
            // Successful publish
            // ---------------------------------------------
            if (data.success) {

                alert("Waiver published successfully");

                setWaiverData((prev) => ({
                    ...prev,
                    waiver: {
                        ...prev.waiver,
                        waiverStatus: "published",
                    },
                }));

                navigate(
                    `/business/waiverbuilder/smart/${waiverId}/step-8`
                );

                return;
            }

            // ---------------------------------------------
            // Other publish errors
            // ---------------------------------------------
            setIsPublishing(false);

            alert(
                data.message ||
                "Failed to publish waiver"
            );

        } catch (err) {

            setIsPublishing(false);

            console.error(
                "Publish failed",
                err
            );
        }
    };


  const handlePublish = () => {

    if (isPublishing) return;

    const isInvalid = Object.values(otherMap).some(v => !v.trim());

    if (isInvalid) {
      alert('Please enter details for all "Other" fields before proceeding.');   
      return;
    }

    setIsPublishing(true);
    setShowPublishConfirm(true);
  };

    return (

        <div className="h-screen flex bg-background">

            <Sidebar />

            <div className="flex-1 overflow-auto">

                <div className="max-w-7xl mx-auto px-8 py-8">

                    <button
                        onClick={() =>
                            navigate(`/business/waivereditor/${waiverId}`)
                        }
                        className="text-sm mb-6 text-gray-500"
                    >
                        ← Back to editor
                    </button>

                    <div className="flex justify-between items-center mb-8">

                        <div>

                            <h1 className="text-3xl font-semibold">
                                Review & Publish
                            </h1>

                            <p className="text-gray-500 mt-1">
                                Review exactly what signers will see before publishing.
                            </p>

                        </div>

                        <button
                            onClick={handlePublish}
                            disabled={isPublishing}
                            className={`rounded-full px-7 py-3 font-medium ${
                                isPublishing
                                    ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                                    : "bg-yellow-400 hover:bg-yellow-500"
                            }`}
                        >
                            {isPublishing ? "Publishing..." : "Publish"}
                        </button>

                    </div>

                    <ChangeSummary
                        summary={waiver.changeSummary}
                        waiver={waiver}
                    />

                    <div className="flex gap-4 mb-8">

                        {/* Adult / Minor */}

                        {/* Full / Quick */}

                        {/* Desktop / Mobile */}

                    </div>

                    <div className="flex justify-center">

                        <WaiverPreview

                            key={`${signerType}_${waiverMode}`}

                            waiverData={waiverData}

                            signerType={signerType}

                            waiverMode={waiverMode}

                            device={device}

                            governingData={governingData}

                            phValues={phValues}

                            otherMap={otherMap}

                            setOtherMap={setOtherMap}

                        />

                    </div>

                </div>

            </div>

            {showPublishConfirm && (
                <ConfirmModal
                open={showPublishConfirm}
                title="Confirm Publish"
                message={getPublishConfirmMessage(waiver?.waiverStatus)}
                confirmText="Publish"
                cancelText="Cancel"
                onConfirm={confirmPublish}
                onCancel={() => {
                    setShowPublishConfirm(false);
                    setIsPublishing(false);
                }}
                />
            )}

        </div>

    );

};

export default WaiverReviewAndPublishEditor;